import { useState, useEffect } from 'react';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';

// A memory cache mapping original URL -> Local File Source
const memoryCache = new Map<string, string>();
// Downloads in progress, so the same file is never fetched twice at once
// (e.g. an avatar shown in several places).
const inflight = new Map<string, Promise<string>>();
// Files that couldn't be cached this session: shown from the network instead
// of being downloaded again every time the screen opens.
const failed = new Set<string>();

// Created once per session. Calling mkdir for every image failed with
// "already exists" each time and crossed the native bridge for nothing.
let cacheDirReady: Promise<void> | null = null;
function ensureCacheDir(): Promise<void> {
    if (!cacheDirReady) {
        cacheDirReady = Filesystem.mkdir({
            path: 'media_cache',
            directory: Directory.Data,
            recursive: true
        }).then(() => undefined, () => undefined);
    }
    return cacheDirReady;
}

// At most two downloads at a time: a feed full of videos otherwise saturated
// the connection while screens were waiting for their data.
const MAX_PARALLEL_DOWNLOADS = 2;
let activeDownloads = 0;
const waiting: (() => void)[] = [];
async function withDownloadSlot<T>(task: () => Promise<T>): Promise<T> {
    if (activeDownloads >= MAX_PARALLEL_DOWNLOADS) {
        await new Promise<void>((resolve) => waiting.push(resolve));
    }
    activeDownloads++;
    try {
        return await task();
    } finally {
        activeDownloads--;
        waiting.shift()?.();
    }
}

function hashCode(str: string) {
    let hash = 0;
    for (let i = 0, len = str.length; i < len; i++) {
        let chr = str.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0;
    }
    return Math.abs(hash).toString(36);
}

async function downloadAndCache(url: string, fileName: string): Promise<string> {
    const path = `media_cache/${fileName}`;

    // Use the cached copy only if it's non-empty. Downloads go to a temp name
    // and are renamed when complete, so an interrupted download (app closed,
    // network drop) can never leave a truncated file that looks "cached".
    try {
        const stat = await Filesystem.stat({
            path: path,
            directory: Directory.Data
        });
        if (stat.size > 0) {
            return Capacitor.convertFileSrc(stat.uri);
        }
        await Filesystem.deleteFile({ path, directory: Directory.Data }).catch(() => { });
    } catch {
        // Not cached yet
    }

    const tempPath = `${path}.part`;
    try {
        await withDownloadSlot(() => Filesystem.downloadFile({
            url: url,
            path: tempPath,
            directory: Directory.Data
        }));

        const tempStat = await Filesystem.stat({
            path: tempPath,
            directory: Directory.Data
        });
        if (!tempStat.size) {
            throw new Error("Downloaded media is empty");
        }

        await Filesystem.rename({
            from: tempPath,
            to: path,
            directory: Directory.Data,
            toDirectory: Directory.Data
        });

        const stat = await Filesystem.stat({
            path: path,
            directory: Directory.Data
        });
        return Capacitor.convertFileSrc(stat.uri);
    } catch (downloadError) {
        await Filesystem.deleteFile({ path: tempPath, directory: Directory.Data }).catch(() => { });
        console.warn("Failed to download via Filesystem", (downloadError as Error)?.message || downloadError);
        throw downloadError;
    }
}

export function useCachedMedia(url: string | undefined): string | undefined {
    const [cachedUrl, setCachedUrl] = useState<string | undefined>(
        url ? memoryCache.get(url) || url : undefined
    );

    useEffect(() => {
        if (!url || !url.startsWith('http') || memoryCache.has(url) || failed.has(url)) {
            return;
        }

        // On web we just fallback to caching via regular HTTP / CDN
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        let isMounted = true;

        async function processMedia() {
            if (!url) return;
            try {
                await ensureCacheDir();

                // Extract a safe filename from the URL token, or just hash the URL entirely
                // Strip query params for extension checking
                const urlWithoutQuery = url.split('?')[0];
                const extMatch = urlWithoutQuery.match(/\.([a-z0-9]+)$/i);
                // Firebase images might not have extension unless encoded, we just default to bin usually
                // but let's try to extract from firebasestorage path which might have .jpg?alt=media
                const altMatch = url.match(/\.([a-z0-9]+)\?alt=media/i);
                const ext = altMatch ? `.${altMatch[1]}` : (extMatch ? `.${extMatch[1]}` : '.bin');

                const safeFileName = `${hashCode(url)}${ext}`;

                let pending = inflight.get(url);
                if (!pending) {
                    pending = downloadAndCache(url, safeFileName).finally(() => inflight.delete(url));
                    inflight.set(url, pending);
                }
                const localSrc = await pending;

                if (!isMounted) return;

                memoryCache.set(url, localSrc);
                setCachedUrl(localSrc);
            } catch (error) {
                // Fallback to original url silently if caching fails
                failed.add(url);
                console.warn("Failed to natively cache media, using default src", error);
            }
        }

        processMedia();

        return () => {
            isMounted = false;
        };
    }, [url]);

    return cachedUrl;
}
